/**
 * ETL (EmpowerID Identity Fabric Tour Language) — Parser.
 *
 * Consumes the token stream produced by the lexer and emits a {@link Tour}
 * passed through {@link validateTour}. Parser raises {@link ParseError}
 * for grammar violations; the validator owns schema-level errors.
 *
 * Hot path is single-pass: each line is consumed exactly once and probed
 * by a small number of indexed lookups instead of repeated `Array.prototype`
 * scans. Public API offers both throw-style ({@link parseTour}) and
 * Result-style ({@link parseTourSafe}) entry points; both share an LRU
 * memoization layer keyed by the input source so repeated parses
 * (e.g. HMR re-evaluation, agent retries) are O(1).
 */

import { tokenize } from './lexer';
import type { Token, LocatorToken, StringToken, OptionToken, WordToken } from './lexer';
import { validateTour } from './tour-types';
import type {
  Tour,
  Step,
  Locator,
  TourPace,
  TourLocaleOverrides,
  EtlVersion,
  Assertion,
  CallStep,
  CaptureStep,
  ForEachStep,
  IfStep,
  LetStep,
  MacroDef,
  ParamDecl,
  Requirement,
  ScopeFilter,
  SetStep,
  TryStep,
} from './tour-types';
import { parseExpr, parseTemplate } from './expr-parser';
import type { Expr } from './expr-types';
import { DEFAULT_LIMITS } from './limits';
import type { EtlLimits } from './limits';

export class ParseError extends Error {
  constructor(
    message: string,
    public readonly sourceLine: number,
    public readonly column?: number,
  ) {
    const where = column !== undefined ? `${sourceLine}:${column}` : `${sourceLine}`;
    super(`[ETL] Parse error on line ${where}: ${message}`);
    this.name = 'ParseError';
  }
}

/* ─────────────────── token cursor ─────────────────── */

class Cursor {
  private pos = 0;
  constructor(private readonly tokens: ReadonlyArray<Token>) {}

  peek(): Token | null {
    return this.tokens[this.pos] ?? null;
  }

  getPosition(): number { return this.pos; }
  setPosition(p: number): void { this.pos = p; }

  isEOF(): boolean { return this.pos >= this.tokens.length; }

  /**
   * Consumes a logical line: an indent token followed by the line tokens
   * up to (and consuming) the trailing newline. Returns null at EOF.
   */
  consumeLogicalLine(): { indent: number; tokens: Token[]; lineNum: number } | null {
    if (this.pos >= this.tokens.length) return null;
    const first = this.tokens[this.pos];
    if (first.kind !== 'indent') return null;
    this.pos++;
    const indent = first.depth;
    const lineNum = first.line;
    const collected: Token[] = [];
    while (this.pos < this.tokens.length) {
      const t = this.tokens[this.pos++];
      if (t.kind === 'newline') break;
      collected.push(t);
    }
    return { indent, tokens: collected, lineNum };
  }
}

/* ─────────────────── token helpers ─────────────────── */

function firstWord(tokens: Token[]): WordToken | null {
  const t = tokens[0];
  return t?.kind === 'word' ? t : null;
}

function collectStrings(tokens: Token[]): string[] {
  const out: string[] = [];
  for (const t of tokens) if (t.kind === 'string') out.push((t as StringToken).value);
  return out;
}

function findLocator(tokens: Token[]): Locator | null {
  for (const t of tokens) {
    if (t.kind === 'locator') return (t as LocatorToken).locator;
    // `$NAME` locator-variable reference — Phase 5. v1 tours never
    // produce this shape (no `$` words appear) so the lookup is safe
    // to keep unconditional.
    if (
      t.kind === 'word' &&
      (t as WordToken).value.startsWith('$') &&
      (t as WordToken).value.length > 1
    ) {
      const name = (t as WordToken).value.slice(1);
      if (/^[a-zA-Z_][a-zA-Z0-9_-]*$/.test(name)) {
        return { varRef: name };
      }
    }
  }
  return null;
}

function findOption(tokens: Token[], key: string): string | null {
  for (const t of tokens) {
    if (t.kind === 'option' && (t as OptionToken).key === key) return (t as OptionToken).value;
  }
  return null;
}

function findOptionNumber(tokens: Token[], key: string, lineNum: number, max: number): number | null {
  const v = findOption(tokens, key);
  if (v === null) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) {
    throw new ParseError(`option "${key}" expects a finite number (got "${v}")`, lineNum);
  }
  if (n < 0 || n > max) {
    throw new ParseError(`option "${key}" out of range [0, ${max}]`, lineNum);
  }
  return n;
}

function lineNumOf(tokens: Token[]): number {
  return tokens[0]?.line ?? 0;
}

function requireLocator(tokens: Token[], stepType: string): Locator {
  const loc = findLocator(tokens);
  if (!loc) {
    throw new ParseError(
      `"${stepType}" step requires a locator (#testid, @label:…, @role.<role>:…)`,
      lineNumOf(tokens),
    );
  }
  return loc;
}

/* ─────────────────── header ─────────────────── */

interface RawHeader {
  id: string;
  pluginId: string;
  title?: string;
  summary?: string;
  guidePath?: string;
  matchRoute?: string;
  demoMode?: 'dry-run' | 'sandbox';
  pace?: TourPace;
  etlVersion?: EtlVersion;
  params?: Array<ParamDecl>;
  requires?: Array<Requirement>;
  onlyFor?: ScopeFilter;
  notFor?: ScopeFilter;
  prerequisites?: string[];
  isLibrary?: boolean;
  onError?: 'skip' | 'abort';
}

/**
 * v2 keywords that must NOT appear as a line-starter in a v1 tour.
 * If the parser sees one, it raises a clear "requires etl-version 2"
 * error rather than the generic "unknown step" message.
 */
const V2_RESERVED_KEYWORDS: ReadonlySet<string> = new Set<string>([
  // Header
  'params', 'requires', 'only-for', 'not-for', 'prerequisites', 'library',
  // Decls
  'let', 'set', 'capture', 'macro', 'include', 'label',
  // Control flow
  'if', 'elseif', 'else', 'end', 'unless', 'when',
  'forEach', 'repeat', 'until', 'in',
  'try', 'catch', 'finally',
  'expect', 'abort', 'goto', 'skip', 'break', 'continue', 'call',
  // Action verbs
  'drag', 'check', 'uncheck', 'pickDate',
  'copy', 'paste', 'waitForRequest', 'waitForIdle',
  // Visual
  'highlight', 'annotate', 'multi-spot', 'zoom', 'pan',
  // Metadata markers
  'chapter',
]);

function parseHeader(cursor: Cursor, limits: EtlLimits): RawHeader {
  const firstLine = cursor.consumeLogicalLine();
  if (!firstLine) throw new ParseError('Empty source', 0);

  const fw = firstWord(firstLine.tokens);
  if (fw?.value !== 'tour') {
    throw new ParseError(
      `Expected "tour" declaration, got "${fw?.value ?? '<empty>'}"`,
      firstLine.lineNum,
    );
  }

  const strs = collectStrings(firstLine.tokens);
  if (strs.length < 2) {
    throw new ParseError(
      'tour declaration needs two quoted strings: "id" for "plugin-id"',
      firstLine.lineNum,
    );
  }

  const header: RawHeader = { id: strs[0], pluginId: strs[1] };
  const seen = new Set<string>();
  const msMax = limits.maxWaitMs;

  while (!cursor.isEOF()) {
    const peek = cursor.peek();
    if (!peek || peek.kind !== 'indent') break;

    const saved = cursor.getPosition();
    const logLine = cursor.consumeLogicalLine();
    if (!logLine) break;

    const kw = firstWord(logLine.tokens)?.value;
    if (!kw) continue;

    if (seen.has(kw) && kw !== 'pace') {
      throw new ParseError(`duplicate header field "${kw}"`, logLine.lineNum);
    }

    switch (kw) {
      case 'title':
        header.title = collectStrings(logLine.tokens)[0] ?? '';
        seen.add(kw);
        break;
      case 'summary':
        header.summary = collectStrings(logLine.tokens)[0] ?? '';
        seen.add(kw);
        break;
      case 'guide':
        header.guidePath = collectStrings(logLine.tokens)[0] ?? '';
        seen.add(kw);
        break;
      case 'on':
        header.matchRoute = collectStrings(logLine.tokens)[0] ?? '';
        seen.add(kw);
        break;
      case 'dry-run':
        header.demoMode = 'dry-run';
        seen.add(kw);
        break;
      case 'sandbox':
        header.demoMode = 'sandbox';
        seen.add(kw);
        break;
      case 'pace': {
        const pace: TourPace = {};
        const ln = logLine.lineNum;
        const settle = findOptionNumber(logLine.tokens, 'settle', ln, msMax);
        const narrate = findOptionNumber(logLine.tokens, 'narrate', ln, msMax);
        const type = findOptionNumber(logLine.tokens, 'type', ln, msMax);
        const cur = findOptionNumber(logLine.tokens, 'cursor', ln, msMax);
        if (settle !== null) pace.settleMs = settle;
        if (narrate !== null) pace.narrateMs = narrate;
        if (type !== null) pace.perCharMs = type;
        if (cur !== null) pace.cursorMs = cur;
        header.pace = pace;
        seen.add(kw);
        break;
      }
      case 'etl-version': {
        // Header keyword `etl-version 2` opts the tour into v2 grammar.
        // The numeric arg is a bareword token (the lexer doesn't treat
        // digits as punctuation), so scan for the first non-keyword
        // word.
        let versionTok: WordToken | null = null;
        for (let i = 1; i < logLine.tokens.length; i++) {
          const t = logLine.tokens[i];
          if (t.kind === 'word') { versionTok = t as WordToken; break; }
        }
        if (!versionTok) {
          throw new ParseError(
            '"etl-version" requires a version number (1 or 2)',
            logLine.lineNum,
          );
        }
        const v = Number(versionTok.value);
        if (v !== 1 && v !== 3) {
          throw new ParseError(
            `etl-version must be 1 or 3 (got "${versionTok.value}")`,
            logLine.lineNum,
          );
        }
        header.etlVersion = v as EtlVersion;
        seen.add(kw);
        break;
      }
      case 'param': {
        if (header.etlVersion !== 3) {
          throw new ParseError(v2KeywordError('param', header.etlVersion), logLine.lineNum);
        }
        const decl = parseParamHeader(logLine.tokens, logLine.lineNum, limits);
        if (!header.params) header.params = [];
        if (header.params.length >= limits.maxParams) {
          throw new ParseError(`tour exceeds ${limits.maxParams} params`, logLine.lineNum);
        }
        if (header.params.some((p) => p.name === decl.name)) {
          throw new ParseError(`duplicate param "${decl.name}"`, logLine.lineNum);
        }
        header.params.push(decl);
        // Note: `param` is repeatable, so we don't add it to `seen`.
        break;
      }
      case 'requires': {
        if (header.etlVersion !== 3) {
          throw new ParseError(v2KeywordError('requires', header.etlVersion), logLine.lineNum);
        }
        const reqs = parseRequiresHeader(logLine.tokens, logLine.lineNum);
        if (!header.requires) header.requires = [];
        header.requires.push(...reqs);
        break;
      }
      case 'only-for':
      case 'not-for': {
        if (header.etlVersion !== 3) {
          throw new ParseError(v2KeywordError(kw, header.etlVersion), logLine.lineNum);
        }
        const filter = parseScopeHeader(logLine.tokens, logLine.lineNum);
        if (kw === 'only-for') header.onlyFor = mergeScope(header.onlyFor, filter);
        else                   header.notFor  = mergeScope(header.notFor,  filter);
        break;
      }
      case 'prerequisites': {
        if (header.etlVersion !== 3) {
          throw new ParseError(v2KeywordError('prerequisites', header.etlVersion), logLine.lineNum);
        }
        const ids = parsePrerequisitesHeader(logLine.tokens, logLine.lineNum);
        if (!header.prerequisites) header.prerequisites = [];
        header.prerequisites.push(...ids);
        break;
      }
      case 'library': {
        if (header.etlVersion !== 3) {
          throw new ParseError(v2KeywordError('library', header.etlVersion), logLine.lineNum);
        }
        if (header.isLibrary) {
          throw new ParseError('duplicate "library" header', logLine.lineNum);
        }
        header.isLibrary = true;
        seen.add(kw);
        break;
      }
      case 'on-error': {
        if (header.etlVersion !== 3) {
          throw new ParseError(v2KeywordError('on-error', header.etlVersion), logLine.lineNum);
        }
        let modeTok: WordToken | null = null;
        for (let i = 1; i < logLine.tokens.length; i++) {
          const t = logLine.tokens[i];
          if (t.kind === 'word') { modeTok = t as WordToken; break; }
        }
        if (!modeTok || (modeTok.value !== 'skip' && modeTok.value !== 'abort')) {
          throw new ParseError(
            '"on-error" must be followed by "skip" or "abort"',
            logLine.lineNum,
          );
        }
        header.onError = modeTok.value as 'skip' | 'abort';
        seen.add(kw);
        break;
      }
      default:
        // Header parsing ends as soon as we see something that isn't a
        // recognized header keyword. v2 step verbs and unsupported v2
        // header keywords (params/requires/etc.) both fall through to
        // the body parser, which already classifies them via
        // V2_RESERVED_KEYWORDS and emits a phase-aware error.
        cursor.setPosition(saved);
        return header;
    }
  }

  return header;
}

function v2KeywordError(kw: string, etlVersion: EtlVersion | undefined): string {
  if (etlVersion === 3) {
    return `"${kw}" is reserved for ETL v3 and will be supported in a later parser release`;
  }
  return `"${kw}" requires "etl-version 3" in the tour header`;
}

/* ─────────────────── steps ─────────────────── */

function parseStep(
  tokens: Token[],
  ln: number,
  limits: EtlLimits,
  etlVersion?: EtlVersion,
): Step {
  const fw = firstWord(tokens);
  if (!fw) throw new ParseError('Empty step line', ln);
  const kw = fw.value;

  const strs = collectStrings(tokens);
  const lastStr = strs.length > 0 ? strs[strs.length - 1] : undefined;
  const msMax = limits.maxWaitMs;

  switch (kw) {
    case 'narrate': {
      const text = strs[0];
      if (!text) throw new ParseError('"narrate" requires a quoted string', ln);
      const hold = findOptionNumber(tokens, 'hold', ln, msMax);
      const step: Step = { type: 'narrate', text };
      if (hold !== null) step.timeoutMs = hold;
      return step;
    }

    case 'navigate': {
      const to = strs[0];
      if (!to) throw new ParseError('"navigate" requires a quoted route string', ln);
      const step: Step = { type: 'navigate', to };
      if (strs[1]) step.narrate = strs[1];
      return step;
    }

    case 'waitFor':
    case 'wait-for': {
      const locator = requireLocator(tokens, 'waitFor');
      const timeout = findOptionNumber(tokens, 'timeout', ln, msMax);
      const step: Step = { type: 'waitFor', locator };
      if (timeout !== null) step.timeoutMs = timeout;
      if (lastStr) step.narrate = lastStr;
      return step;
    }

    case 'scrollTo':
    case 'scroll-to': {
      const locator = requireLocator(tokens, 'scrollTo');
      const step: Step = { type: 'scrollTo', locator };
      if (lastStr) step.narrate = lastStr;
      return step;
    }

    case 'move': {
      const locator = requireLocator(tokens, 'move');
      const step: Step = { type: 'move', locator };
      if (lastStr) step.narrate = lastStr;
      return step;
    }

    case 'hover': {
      const locator = requireLocator(tokens, 'hover');
      const step: Step = { type: 'hover', locator };
      if (lastStr) step.narrate = lastStr;
      return step;
    }

    case 'click': {
      const locator = requireLocator(tokens, 'click');
      const step: Step = { type: 'click', locator };
      if (lastStr) step.narrate = lastStr;
      return step;
    }

    case 'stop-at':
    case 'stopAt': {
      const locator = requireLocator(tokens, 'stop-at');
      const step: Step = { type: 'click', locator, execute: false };
      if (lastStr) step.narrate = lastStr;
      return step;
    }

    case 'type': {
      const locator = requireLocator(tokens, 'type');
      const text = strs[0];
      if (text === undefined) throw new ParseError('"type" requires a quoted text string', ln);
      const narrate = strs[1];
      const speed = findOptionNumber(tokens, 'speed', ln, msMax);
      const settle = findOptionNumber(tokens, 'settle', ln, msMax);
      const step: Step = { type: 'type', locator, text };
      if (narrate) step.narrate = narrate;
      if (speed !== null) step.perCharMs = speed;
      if (settle !== null) step.settleMs = settle;
      return step;
    }

    case 'press': {
      // First word is "press"; the next word token is the key.
      let keyTok: WordToken | null = null;
      for (let i = 1; i < tokens.length; i++) {
        if (tokens[i].kind === 'word') { keyTok = tokens[i] as WordToken; break; }
      }
      if (!keyTok) {
        throw new ParseError(
          '"press" requires a key name (e.g. Tab, Enter)',
          ln,
        );
      }
      const step: Step = { type: 'press', key: keyTok.value };
      const loc = findLocator(tokens);
      if (loc) step.locator = loc;
      if (lastStr) step.narrate = lastStr;
      return step;
    }

    case 'select': {
      const locator = requireLocator(tokens, 'select');
      const value = findOption(tokens, 'value');
      const selectOption = findOption(tokens, 'option');
      const settle = findOptionNumber(tokens, 'settle', ln, msMax);
      const step: Step = { type: 'select', locator };
      if (value !== null) step.value = value;
      if (selectOption !== null) step.selectOption = selectOption;
      if (lastStr) step.narrate = lastStr;
      if (settle !== null) step.settleMs = settle;
      return step;
    }

    case 'wait': {
      let ms = 0;
      for (let i = 1; i < tokens.length; i++) {
        const t = tokens[i];
        if (t.kind === 'word' && /^\d+$/.test(t.value)) {
          const parsed = Number(t.value);
          if (parsed > msMax) {
            throw new ParseError(`wait ms (${parsed}) exceeds limit ${msMax}`, ln);
          }
          ms = parsed;
          break;
        }
      }
      const step: Step = { type: 'wait', ms };
      if (lastStr) step.narrate = lastStr;
      return step;
    }

    case 'expect': {
      if (etlVersion !== 3) {
        throw new ParseError(v2KeywordError('expect', etlVersion), ln);
      }
      return parseExpectStep(tokens, ln, limits);
    }

    /* ── Phase 6 step verbs (v2 only) ───────────────────────────── */
    case 'highlight': {
      if (etlVersion !== 3) throw new ParseError(v2KeywordError('highlight', etlVersion), ln);
      const locator = requireLocator(tokens, 'highlight');
      const step: Step = { type: 'highlight', locator };
      if (lastStr) step.narrate = lastStr;
      return step;
    }
    case 'chapter': {
      if (etlVersion !== 3) throw new ParseError(v2KeywordError('chapter', etlVersion), ln);
      const title = strs[0];
      if (!title) throw new ParseError('"chapter" requires a quoted title', ln);
      return { type: 'chapter', title };
    }
    case 'check': {
      if (etlVersion !== 3) throw new ParseError(v2KeywordError('check', etlVersion), ln);
      const locator = requireLocator(tokens, 'check');
      const step: Step = { type: 'check', locator };
      if (lastStr) step.narrate = lastStr;
      return step;
    }
    case 'uncheck': {
      if (etlVersion !== 3) throw new ParseError(v2KeywordError('uncheck', etlVersion), ln);
      const locator = requireLocator(tokens, 'uncheck');
      const step: Step = { type: 'uncheck', locator };
      if (lastStr) step.narrate = lastStr;
      return step;
    }
    case 'drag': {
      if (etlVersion !== 3) throw new ParseError(v2KeywordError('drag', etlVersion), ln);
      // `drag <from> to <to>` — two locators separated by the literal
      // word `to`. The lexer doesn't treat `to` specially, so we walk
      // the tokens and pair the first two locators we find.
      const locators: Locator[] = [];
      for (const t of tokens) {
        if (t.kind === 'locator') locators.push((t as LocatorToken).locator);
        if (
          t.kind === 'word' &&
          (t as WordToken).value.startsWith('$') &&
          (t as WordToken).value.length > 1
        ) {
          const name = (t as WordToken).value.slice(1);
          if (/^[a-zA-Z_][a-zA-Z0-9_-]*$/.test(name)) {
            locators.push({ varRef: name });
          }
        }
        if (locators.length === 2) break;
      }
      if (locators.length < 2) {
        throw new ParseError('"drag" requires two locators (drag <from> to <to>)', ln);
      }
      const step: Step = { type: 'drag', from: locators[0], to: locators[1] };
      if (lastStr) step.narrate = lastStr;
      return step;
    }
    case 'multi-spot': {
      if (etlVersion !== 3) throw new ParseError(v2KeywordError('multi-spot', etlVersion), ln);
      const locators: Locator[] = [];
      for (const t of tokens) {
        if (t.kind === 'locator') locators.push((t as LocatorToken).locator);
        if (
          t.kind === 'word' &&
          (t as WordToken).value.startsWith('$') &&
          (t as WordToken).value.length > 1
        ) {
          const name = (t as WordToken).value.slice(1);
          if (/^[a-zA-Z_][a-zA-Z0-9_-]*$/.test(name)) {
            locators.push({ varRef: name });
          }
        }
      }
      if (locators.length === 0) {
        throw new ParseError('"multi-spot" requires at least one locator', ln);
      }
      const step: Step = { type: 'multi-spot', locators };
      if (lastStr) step.narrate = lastStr;
      return step;
    }
    case 'annotate': {
      if (etlVersion !== 3) throw new ParseError(v2KeywordError('annotate', etlVersion), ln);
      const locator = requireLocator(tokens, 'annotate');
      // Annotation text is the FIRST quoted string after the locator.
      // (The `lastStr` heuristic used elsewhere takes the trailing
      // narration; for annotate the primary string IS the annotation.)
      const annotationText = strs[0];
      if (annotationText === undefined) {
        throw new ParseError('"annotate" requires a quoted annotation text', ln);
      }
      const step: Step = { type: 'annotate', locator, text: annotationText };
      // `placement:` option for axis hint (right-of, etc.)
      const placement = findOption(tokens, 'placement');
      if (placement) {
        const valid = ['right-of', 'left-of', 'above', 'below', 'near'];
        if (!valid.includes(placement)) {
          throw new ParseError(
            `placement must be one of: ${valid.join(', ')}`,
            ln,
          );
        }
        (step as { placement?: 'right-of' | 'left-of' | 'above' | 'below' | 'near' })
          .placement = placement as 'right-of' | 'left-of' | 'above' | 'below' | 'near';
      }
      const sticky = findOption(tokens, 'sticky');
      if (sticky === 'true') (step as { sticky?: boolean }).sticky = true;
      return step;
    }

    default:
      if (V2_RESERVED_KEYWORDS.has(kw)) {
        throw new ParseError(v2KeywordError(kw, etlVersion), ln);
      }
      throw new ParseError(`Unknown step type "${kw}"`, ln);
  }
}

/* ─────────────────── expect step (ETL v2) ─────────────────── */

const ASSERT_KIND_WORDS: ReadonlyMap<string, Assertion['kind']> = new Map<string, Assertion['kind']>([
  ['visible', 'visible'],
  ['not-visible', 'not-visible'],
  ['exists', 'exists'],
  ['not-exists', 'not-exists'],
  ['enabled', 'enabled'],
  ['disabled', 'disabled'],
  ['text', 'text'],
  ['value', 'value'],
  ['count', 'count'],
]);

const TEXT_OPS: ReadonlySet<string> = new Set(['equals', 'contains']);
const COUNT_OPS: ReadonlySet<string> = new Set(['==', '!=', '<', '<=', '>', '>=']);

/**
 * Parses one of the assertion forms accepted by `expect`. v2 only.
 *
 * ```
 *   expect VISIBLE | NOT-VISIBLE | EXISTS | NOT-EXISTS
 *          | ENABLED | DISABLED  <locator>  [soft:true]
 *   expect TEXT  <locator> (equals|contains) <"string">
 *   expect VALUE <locator> (equals|contains) <"string">
 *   expect COUNT <locator> (==|!=|<|<=|>|>=) <integer>
 * ```
 *
 * `soft:true` flips the assertion into "narrate-then-continue" mode —
 * the runtime records a soft failure on the log but does not abort the
 * tour. Default is hard fail (matches assert semantics in test
 * frameworks).
 */
function parseExpectStep(tokens: Token[], ln: number, limits: EtlLimits): Step {
  // Locate the kind word — first `word` token after "expect".
  let kindIdx = -1;
  for (let i = 1; i < tokens.length; i++) {
    if (tokens[i].kind === 'word') { kindIdx = i; break; }
  }
  if (kindIdx === -1) {
    throw new ParseError(
      '"expect" requires an assertion kind (VISIBLE, NOT-VISIBLE, EXISTS, NOT-EXISTS, ENABLED, DISABLED, TEXT, VALUE, COUNT)',
      ln,
    );
  }
  const kindWord = tokens[kindIdx] as WordToken;
  const kindKey = kindWord.value.toLowerCase();
  const kind = ASSERT_KIND_WORDS.get(kindKey);
  if (!kind) {
    throw new ParseError(
      `unknown expect kind "${kindWord.value}" (allowed: VISIBLE, NOT-VISIBLE, EXISTS, NOT-EXISTS, ENABLED, DISABLED, TEXT, VALUE, COUNT)`,
      ln,
    );
  }

  // Locator must come after the kind word. Accepts both lexer-emitted
  // LocatorTokens (`#testid`, `@label:…`) and `$varName` word tokens
  // (Phase 5 locator-variable references resolved at run time).
  let locatorIdx = -1;
  let locator: Locator | null = null;
  for (let i = kindIdx + 1; i < tokens.length; i++) {
    const t = tokens[i];
    if (t.kind === 'locator') {
      locator = (t as LocatorToken).locator;
      locatorIdx = i;
      break;
    }
    if (
      t.kind === 'word' &&
      (t as WordToken).value.startsWith('$') &&
      (t as WordToken).value.length > 1
    ) {
      const name = (t as WordToken).value.slice(1);
      if (/^[a-zA-Z_][a-zA-Z0-9_-]*$/.test(name)) {
        locator = { varRef: name };
        locatorIdx = i;
        break;
      }
    }
  }
  if (!locator) {
    throw new ParseError(
      `"expect ${kindWord.value}" requires a locator (#testid, @label:…, @role.<role>:…, $varName)`,
      ln,
    );
  }

  const assertion: Assertion = { kind, locator };
  let narrateText: string | undefined;

  if (kind === 'text' || kind === 'value') {
    // Operator + expected string come AFTER the locator. The narration
    // string is the LAST quoted string on the line; the expected value
    // is the FIRST quoted string after the locator.
    let opTok: WordToken | null = null;
    let expectedTok: StringToken | null = null;
    for (let i = locatorIdx + 1; i < tokens.length; i++) {
      const t = tokens[i];
      if (!opTok && t.kind === 'word' && TEXT_OPS.has(t.value.toLowerCase())) {
        opTok = t as WordToken;
      } else if (opTok && !expectedTok && t.kind === 'string') {
        expectedTok = t as StringToken;
      }
    }
    if (!opTok) {
      throw new ParseError(
        `"expect ${kindWord.value}" requires "equals" or "contains" before the expected value`,
        ln,
      );
    }
    if (!expectedTok) {
      throw new ParseError(
        `"expect ${kindWord.value} ${opTok.value}" requires a quoted expected value`,
        ln,
      );
    }
    if (expectedTok.value.length > limits.maxStringLength) {
      throw new ParseError(
        `expected value exceeds ${limits.maxStringLength} chars`,
        ln,
      );
    }
    (assertion as { op?: Assertion['op'] }).op = opTok.value.toLowerCase() as Assertion['op'];
    (assertion as { expected?: Assertion['expected'] }).expected = expectedTok.value;

    // Narration: a quoted string AFTER the expected value, if any.
    for (let i = tokens.indexOf(expectedTok) + 1; i < tokens.length; i++) {
      const t = tokens[i];
      if (t.kind === 'string') { narrateText = (t as StringToken).value; break; }
    }
  } else if (kind === 'count') {
    let opTok: WordToken | null = null;
    let nTok: WordToken | null = null;
    for (let i = locatorIdx + 1; i < tokens.length; i++) {
      const t = tokens[i];
      if (!opTok && t.kind === 'word' && COUNT_OPS.has(t.value)) {
        opTok = t as WordToken;
      } else if (opTok && !nTok && t.kind === 'word' && /^\d+$/.test(t.value)) {
        nTok = t as WordToken;
      }
    }
    if (!opTok) {
      throw new ParseError(
        `"expect COUNT" requires a comparison operator (== != < <= > >=) after the locator`,
        ln,
      );
    }
    if (!nTok) {
      throw new ParseError(
        `"expect COUNT ${opTok.value}" requires an integer after the operator`,
        ln,
      );
    }
    (assertion as { op?: Assertion['op'] }).op = opTok.value as Assertion['op'];
    (assertion as { expected?: Assertion['expected'] }).expected = Number(nTok.value);

    // Narration is the last quoted string on the line, if any.
    for (let i = tokens.length - 1; i > locatorIdx; i--) {
      if (tokens[i].kind === 'string') {
        narrateText = (tokens[i] as StringToken).value;
        break;
      }
    }
  } else {
    // Pure-locator assertions (visible / not-visible / exists / not-exists / enabled / disabled).
    // Optional trailing narration string.
    for (let i = tokens.length - 1; i > locatorIdx; i--) {
      if (tokens[i].kind === 'string') {
        narrateText = (tokens[i] as StringToken).value;
        break;
      }
    }
  }

  // Soft-fail flag (`soft:true`).
  const soft = findOption(tokens, 'soft');
  if (soft === 'true') (assertion as { softFail?: boolean }).softFail = true;
  else if (soft !== null && soft !== 'false') {
    throw new ParseError(`"soft" option must be "true" or "false" (got "${soft}")`, ln);
  }

  const step: Step = { type: 'expect', assertion };
  if (narrateText) step.narrate = narrateText;
  return step;
}

/* ─────────────────── stable IDs ─────────────────── */

const ID_PATTERN = /^[a-z][a-z0-9-]*$/;

/**
 * Reads `retry:N` and `backoff:Nms` options from any v2 step's option
 * suffix. Phase 2 introduces the lifecycle wrapper that consumes
 * these. Gated on `etlVersion === 3` so v1 tours can never accidentally
 * inherit retry semantics.
 */
function readRetryOptions(
  tokens: Token[],
  ln: number,
  limits: EtlLimits,
  etlVersion: EtlVersion | undefined,
): { retry?: number; backoffMs?: number } {
  const retry = findOption(tokens, 'retry');
  const backoff = findOption(tokens, 'backoff');
  if (retry === null && backoff === null) return {};
  if (etlVersion !== 3) {
    throw new ParseError(
      v2KeywordError(retry !== null ? 'retry:' : 'backoff:', etlVersion),
      ln,
    );
  }
  const out: { retry?: number; backoffMs?: number } = {};
  if (retry !== null) {
    const n = Number(retry);
    if (!Number.isInteger(n) || n < 0 || n > 16) {
      throw new ParseError(
        `"retry" must be an integer in [0, 16] (got "${retry}")`,
        ln,
      );
    }
    out.retry = n;
  }
  if (backoff !== null) {
    const n = Number(backoff);
    if (!Number.isFinite(n) || n < 0 || n > limits.maxWaitMs) {
      throw new ParseError(
        `"backoff" must be a non-negative number of milliseconds <= ${limits.maxWaitMs} (got "${backoff}")`,
        ln,
      );
    }
    out.backoffMs = n;
  }
  return out;
}

/**
 * Author-supplied step id. Read from the `id:snake-name` option suffix
 * on any step. Must match {@link ID_PATTERN}; throws otherwise so a
 * typo in the author's id surfaces immediately rather than silently
 * shadowing the auto-generated id.
 */
function readAuthorId(tokens: Token[], ln: number, limits: EtlLimits): string | undefined {
  const v = findOption(tokens, 'id');
  if (v === null) return undefined;
  if (v.length === 0) {
    throw new ParseError('"id:" requires a non-empty value', ln);
  }
  if (v.length > limits.maxIdentifierLength) {
    throw new ParseError(
      `step id "${v}" exceeds ${limits.maxIdentifierLength} chars`,
      ln,
    );
  }
  if (!ID_PATTERN.test(v)) {
    throw new ParseError(
      `step id "${v}" must match /^[a-z][a-z0-9-]*$/ (snake-kebab)`,
      ln,
    );
  }
  return v;
}

// Auto stable-id assignment is tree-aware in v2 — see {@link assignStableIds}.
// (`validateTour` still fills in a flat `auto:N` for JSON-loaded tours that
// never reach the parser.)

/* ─────────────────── locale overlay ─────────────────── */

/**
 * Parses a **locale overlay** file: only `title`, `summary`, and `[n] "…"`
 * lines (same syntax as the body of an old inline `locale` block, without
 * the `locale "xx"` header). Use with a companion base tour file, e.g.
 * `create-auth-account.etl` + `create-auth-account-es.etl`.
 */
export function parseLocaleOverlay(
  source: string,
  options: { limits?: EtlLimits } = {},
): TourLocaleOverrides {
  const limits = options.limits ?? DEFAULT_LIMITS;
  const tokens = tokenize(source, { limits });
  const cursor = new Cursor(tokens);
  const override: TourLocaleOverrides = {};
  const stepOverrides: TourLocaleOverrides['steps'] = [];

  while (!cursor.isEOF()) {
    const logLine = cursor.consumeLogicalLine();
    if (!logLine) break;
    if (logLine.tokens.length === 0) continue;

    const childKw = firstWord(logLine.tokens)?.value;
    if (!childKw) continue;

    switch (childKw) {
      case 'title':
        override.title = collectStrings(logLine.tokens)[0] ?? '';
        break;
      case 'summary':
        override.summary = collectStrings(logLine.tokens)[0] ?? '';
        break;
      default: {
        const m = /^\[(\d+)\]$/.exec(childKw);
        if (m) {
          const idx = Number(m[1]);
          if (idx > limits.maxSteps) {
            throw new ParseError(
              `step index ${idx} exceeds limit ${limits.maxSteps}`,
              logLine.lineNum,
            );
          }
          const narrate = collectStrings(logLine.tokens)[0];
          if (narrate !== undefined) stepOverrides[idx] = { narrate };
        } else {
          throw new ParseError(
            `locale overlay: expected title, summary, or [n] step index, got "${childKw}"`,
            logLine.lineNum,
          );
        }
      }
    }
  }

  if (stepOverrides.length > 0) override.steps = stepOverrides;
  return override;
}

/**
 * If `filename` matches `{anything}-{locale}.etl` (locale = BCP47-like
 * segment), returns the base filename and locale key. Otherwise `null`.
 *
 * @example matchLocaleOverlayBasename("create-auth-account-es.etl")
 *   → { baseFileName: "create-auth-account.etl", locale: "es" }
 */
export function matchLocaleOverlayBasename(
  filename: string,
): { baseFileName: string; locale: string } | null {
  const base = filename.replace(/^.*[/\\]/, '');
  const m = /^(.+)-([a-z]{2}(?:-[a-z]{2})?)\.etl$/i.exec(base);
  if (!m) return null;
  return { baseFileName: `${m[1]}.etl`, locale: m[2].toLowerCase() };
}

/* ─────────────────── ETL v2 — block-aware body parsing ─────────── */

interface ParserCtx {
  cursor: Cursor;
  limits: EtlLimits;
  etlVersion: EtlVersion | undefined;
  /** Original source split by `\n`. Used to extract expression slices and decl right-hand-sides. */
  sourceLines: string[];
  /** Tracks author-supplied step ids so duplicates can be rejected across the whole tour. */
  authorIds: Set<string>;
  /**
   * Macros defined directly in this tour OR inherited from any
   * `include "<path>"` directive. Resolved by name at `call` time.
   */
  macros: Map<string, MacroDef>;
  /**
   * Library file paths the tour pulls macros from. Resolution is the
   * host catalog's responsibility — the parser only records what was
   * authored. Each path is a string literal as authored.
   */
  includes: string[];
}

/** Result of parsing a body of steps. The terminator (if any) has been consumed. */
interface BodyResult {
  steps: Step[];
  terminator: { kw: string; lineNum: number; tokens: Token[] } | null;
}

/**
 * Block-opener keywords that recursively call `parseStepsBody`. The
 * first-word dispatch in `parseStepsBody` consults this so adding a new
 * opener keeps to one place. `macro` is a top-level-only opener (its
 * body is not a step list — it's a separate macro definition); see
 * {@link parseMacroDef}.
 */
const BLOCK_OPENERS: ReadonlySet<string> = new Set([
  'if', 'forEach', 'try',
]);

/**
 * Decl keywords. Like block openers but produce a single Step rather
 * than recursing.
 */
const DECL_OPENERS: ReadonlySet<string> = new Set([
  'let', 'set', 'capture',
]);

/** Top-level-only directives consumed before normal body parsing. */
const TOP_LEVEL_DIRECTIVES: ReadonlySet<string> = new Set([
  'macro', 'include',
]);

/**
 * Loop-control verbs. Single-word steps with no body. Each is gated on
 * `etl-version 2` and only meaningful inside a `forEach` body — the
 * runner enforces that at execution time, not the parser, so authors
 * can split macros into reusable fragments.
 */
const LOOP_CONTROL_VERBS: ReadonlySet<string> = new Set([
  'break', 'continue', 'skip',
]);

/**
 * Block terminators. When `parseStepsBody` sees one of these as the
 * first word of a line, it returns control to the caller — the caller
 * is the block opener that knows what to do with each terminator.
 *
 * If a terminator appears at top level (no enclosing opener),
 * `parseStepsBody` raises a clear error.
 */
const BLOCK_TERMINATORS: ReadonlySet<string> = new Set([
  'end', 'else', 'elseif', 'catch', 'finally',
]);

const IDENT_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_-]*$/;

/**
 * Returns the substring of `sourceLines[lineNum-1]` strictly between
 * the leading keyword and the first option-shaped token (`key:value`)
 * — or end-of-line if there are no options. Inline comments are
 * stripped per the lexer's rule (` # ` or ` #\n`).
 *
 * `lineTokens` is the already-lexed token array for the line; we use
 * it to locate the first option token's column without reparsing.
 * Block openers (`if EXPR`, `forEach VAR in EXPR`) and decl openers
 * (`let X = EXPR`) all need this so step-level options like `id:`,
 * `retry:`, and `backoff:` appearing after the expression don't get
 * accidentally fed to the expression parser.
 */
function sliceExprSource(
  sourceLines: string[],
  lineNum: number,
  keyword: string,
  lineTokens: Token[] | undefined,
  ln: number,
): { source: string; column: number } {
  const raw = sourceLines[lineNum - 1] ?? '';
  const indentMatch = raw.match(/^\s*/);
  const indent = indentMatch ? indentMatch[0].length : 0;
  const slice = raw.slice(indent);
  if (!slice.startsWith(keyword)) {
    throw new ParseError(
      `internal: expected "${keyword}" at line start (line "${slice.slice(0, 40)}")`,
      ln,
    );
  }
  // Stop before the first OptionToken on the line, if any.
  let endIdx = raw.length;
  if (lineTokens) {
    for (const t of lineTokens) {
      if (t.kind === 'option' && t.column !== undefined) {
        // column is 1-based; convert to 0-based exclusive end.
        endIdx = Math.max(indent + keyword.length, indent + t.column - 1);
        break;
      }
    }
  }
  const startIdx = indent + keyword.length;
  let after = raw.slice(startIdx, endIdx);
  const commentMatch = after.match(/\s#(?:\s|$)/);
  if (commentMatch && commentMatch.index !== undefined) {
    after = after.slice(0, commentMatch.index);
  }
  return { source: after.trim(), column: startIdx + 1 };
}

/**
 * Convenience: extract the expression source after a leading keyword
 * and parse it. The column passed to `parseExpr` is 1-based for error
 * messages.
 */
function parseExprAfterKeyword(
  pctx: ParserCtx,
  line: { tokens: Token[]; lineNum: number },
  keyword: string,
): Expr {
  const { source, column } = sliceExprSource(
    pctx.sourceLines,
    line.lineNum,
    keyword,
    line.tokens,
    line.lineNum,
  );
  if (source.length === 0) {
    throw new ParseError(`"${keyword}" requires an expression`, line.lineNum);
  }
  return parseExpr(source, { line: line.lineNum, column, limits: pctx.limits });
}

/** Asserts that v2-only constructs are gated on `etl-version 2`. */
function requireV2(pctx: ParserCtx, kw: string, ln: number): void {
  if (pctx.etlVersion !== 3) {
    throw new ParseError(v2KeywordError(kw, pctx.etlVersion), ln);
  }
}

/* ─────────────── decl parsers (let / set / capture) ────────────── */

function parseLetDecl(pctx: ParserCtx, line: { tokens: Token[]; lineNum: number }): LetStep | SetStep {
  requireV2(pctx, 'let', line.lineNum);
  // tokens[0] is `let`, tokens[1] is the variable name (a word or
  // option-shaped if user wrote `name:value` — only the bareword form
  // is accepted), then `=`, then the value source which we recover
  // from the raw line text.
  const nameTok = line.tokens[1];
  if (!nameTok || nameTok.kind !== 'word' || !IDENT_PATTERN.test(nameTok.value)) {
    throw new ParseError(
      '"let" requires a bareword variable name (e.g. "let saveBtn = …")',
      line.lineNum,
    );
  }
  const name = nameTok.value;
  // After the name, expect `=` then the expression source.
  const eqIdx = line.tokens.findIndex((t, i) => i > 1 && t.kind === 'word' && t.value === '=');
  if (eqIdx === -1) {
    throw new ParseError('"let" requires "=" before the value', line.lineNum);
  }

  // The value is whatever follows `=` on the line. Two shapes:
  //   - locator literal:  let saveBtn = #save
  //   - expression:       let count = length(name)
  // Locator is the cleaner authoring path for binding to a specific
  // element; expression covers everything else.
  const afterEq = line.tokens.slice(eqIdx + 1);
  const firstAfter = afterEq[0];
  if (firstAfter && firstAfter.kind === 'locator') {
    return {
      type: 'let',
      name,
      value: { locator: { kind: 'step', locator: (firstAfter as LocatorToken).locator } },
    };
  }

  // Expression form. Recover the source text after the `=` sign,
  // stopping at the first option token (so trailing `id:`/`retry:`/
  // `backoff:` don't leak into the expression).
  const valueSource = recoverExprSourceAfterEq(pctx.sourceLines, line, name);
  const expr = parseExpr(valueSource, { line: line.lineNum, limits: pctx.limits });
  return { type: 'let', name, value: expr };
}

function parseSetDecl(pctx: ParserCtx, line: { tokens: Token[]; lineNum: number }): SetStep {
  requireV2(pctx, 'set', line.lineNum);
  const nameTok = line.tokens[1];
  if (!nameTok || nameTok.kind !== 'word' || !IDENT_PATTERN.test(nameTok.value)) {
    throw new ParseError(
      '"set" requires a bareword variable name',
      line.lineNum,
    );
  }
  const name = nameTok.value;
  const eqIdx = line.tokens.findIndex((t, i) => i > 1 && t.kind === 'word' && t.value === '=');
  if (eqIdx === -1) {
    throw new ParseError('"set" requires "=" before the value', line.lineNum);
  }
  const valueSource = recoverExprSourceAfterEq(pctx.sourceLines, line, name);
  const expr = parseExpr(valueSource, { line: line.lineNum, limits: pctx.limits });
  return { type: 'set', name, value: expr };
}

/**
 * Recovers the right-hand-side expression source from a `let X = …` or
 * `set X = …` line. The leading `<keyword> X =` portion is stripped;
 * trailing step-level options (`id:`, `retry:`, `backoff:`) are
 * removed by stopping at the first OptionToken on the line. Inline
 * comments are stripped per the lexer's rule.
 */
function recoverExprSourceAfterEq(
  sourceLines: string[],
  line: { tokens: Token[]; lineNum: number },
  varName: string,
): string {
  const raw = sourceLines[line.lineNum - 1] ?? '';
  const indentMatch = raw.match(/^\s*/);
  const indent = indentMatch ? indentMatch[0].length : 0;
  const body = raw.slice(indent);
  // Find `=` outside any string.
  let inStr = false;
  let eqIdx = -1;
  for (let i = 0; i < body.length; i++) {
    const c = body[i];
    if (inStr) {
      if (c === '\\' && i + 1 < body.length) { i++; continue; }
      if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') { inStr = true; continue; }
    if (c === '=') { eqIdx = i; break; }
  }
  if (eqIdx === -1) {
    throw new ParseError(`internal: expected "=" while extracting "${varName}" value`, line.lineNum);
  }
  // Slice from after `=` to the first option token. Lexer columns are
  // 1-based and post-indent; the absolute 0-based index is
  // `indent + column - 1`.
  let endIdx = raw.length;
  for (const t of line.tokens) {
    if (t.kind === 'option' && t.column !== undefined) {
      endIdx = Math.max(indent + eqIdx + 1, indent + t.column - 1);
      break;
    }
  }
  let after = raw.slice(indent + eqIdx + 1, endIdx);
  const commentMatch = after.match(/\s#(?:\s|$)/);
  if (commentMatch && commentMatch.index !== undefined) {
    after = after.slice(0, commentMatch.index);
  }
  const trimmed = after.trim();
  if (trimmed.length === 0) {
    throw new ParseError(
      `value expected after "= " for "${varName}"`,
      line.lineNum,
    );
  }
  return trimmed;
}

function parseCaptureDecl(pctx: ParserCtx, line: { tokens: Token[]; lineNum: number }): CaptureStep {
  requireV2(pctx, 'capture', line.lineNum);
  // `capture <locator> as <name> [text|value|count]`
  const locTok = line.tokens.find((t) => t.kind === 'locator') as LocatorToken | undefined;
  if (!locTok) {
    throw new ParseError('"capture" requires a locator', line.lineNum);
  }
  // Find `as` keyword after the locator, then the name.
  const locIdx = line.tokens.indexOf(locTok);
  let asIdx = -1;
  for (let i = locIdx + 1; i < line.tokens.length; i++) {
    const t = line.tokens[i];
    if (t.kind === 'word' && t.value === 'as') { asIdx = i; break; }
  }
  if (asIdx === -1) {
    throw new ParseError('"capture" requires "as <name>" after the locator', line.lineNum);
  }
  const nameTok = line.tokens[asIdx + 1];
  if (!nameTok || nameTok.kind !== 'word' || !IDENT_PATTERN.test(nameTok.value)) {
    throw new ParseError('"capture … as" requires a bareword variable name', line.lineNum);
  }
  // Optional `text|value|count` — the LAST word token after the name.
  let what: 'text' | 'value' | 'count' | undefined;
  for (let i = asIdx + 2; i < line.tokens.length; i++) {
    const t = line.tokens[i];
    if (t.kind === 'word' && (t.value === 'text' || t.value === 'value' || t.value === 'count')) {
      what = t.value;
      break;
    }
  }
  const step: CaptureStep = {
    type: 'capture',
    locator: locTok.locator,
    as: nameTok.value,
  };
  if (what) step.what = what;
  return step;
}

/* ─────────────── block parsers (if / forEach / try) ───────────── */

function parseIfBlock(
  pctx: ParserCtx,
  openLine: { tokens: Token[]; lineNum: number },
  depth: number,
): IfStep {
  requireV2(pctx, 'if', openLine.lineNum);
  const test = parseExprAfterKeyword(pctx, openLine, 'if');

  const branches: Array<{ test: Expr; body: ReadonlyArray<Step> }> = [];
  let otherwise: ReadonlyArray<Step> | undefined;
  const afterIfOrElseif = new Set(['elseif', 'else', 'end']);

  let currentTest: Expr = test;
  while (true) {
    const result = parseStepsBody(pctx, depth + 1, afterIfOrElseif);
    if (!result.terminator) {
      throw new ParseError(
        '"if" block was not closed — expected "end"',
        openLine.lineNum,
      );
    }
    branches.push({ test: currentTest, body: result.steps });

    if (result.terminator.kw === 'end') break;
    if (result.terminator.kw === 'elseif') {
      currentTest = parseExprAfterKeyword(
        pctx,
        { tokens: result.terminator.tokens, lineNum: result.terminator.lineNum },
        'elseif',
      );
      continue;
    }
    // 'else' — final body until 'end'
    const elseRes = parseStepsBody(pctx, depth + 1, new Set(['end']));
    if (!elseRes.terminator || elseRes.terminator.kw !== 'end') {
      throw new ParseError('"else" block was not closed — expected "end"', openLine.lineNum);
    }
    otherwise = elseRes.steps;
    break;
  }

  const out: IfStep = { type: 'if', branches };
  if (otherwise !== undefined) out.otherwise = otherwise;
  return out;
}

function parseForEachBlock(
  pctx: ParserCtx,
  openLine: { tokens: Token[]; lineNum: number },
  depth: number,
): ForEachStep {
  requireV2(pctx, 'forEach', openLine.lineNum);
  // `forEach <name> in <expr>` — extract <name> from tokens[1], then
  // expect tokens[2] === 'in', then everything after `in` is the
  // source expression.
  const nameTok = openLine.tokens[1];
  if (!nameTok || nameTok.kind !== 'word' || !IDENT_PATTERN.test(nameTok.value)) {
    throw new ParseError(
      '"forEach" requires a bareword loop variable (e.g. "forEach r in rows")',
      openLine.lineNum,
    );
  }
  const inTok = openLine.tokens[2];
  if (!inTok || inTok.kind !== 'word' || inTok.value !== 'in') {
    throw new ParseError(
      '"forEach <name>" must be followed by "in <expression>"',
      openLine.lineNum,
    );
  }
  // Recover the expression source: everything after `<name> in` on
  // the raw line, stopped at the first option token (so trailing
  // step-level options don't leak into the expression).
  const raw = pctx.sourceLines[openLine.lineNum - 1] ?? '';
  const indent = (raw.match(/^\s*/) ?? [''])[0].length;
  const body = raw.slice(indent);
  const reM = body.match(new RegExp(`^forEach\\s+${nameTok.value}\\s+in\\b`));
  if (!reM) {
    throw new ParseError(
      'internal: failed to recover forEach source',
      openLine.lineNum,
    );
  }
  const startCol = indent + reM[0].length;
  let endIdx = raw.length;
  for (const t of openLine.tokens) {
    if (t.kind === 'option' && t.column !== undefined) {
      const absStart = indent + t.column - 1;
      if (absStart > startCol) {
        endIdx = absStart;
        break;
      }
    }
  }
  let after = raw.slice(startCol, endIdx);
  const commentMatch = after.match(/\s#(?:\s|$)/);
  if (commentMatch && commentMatch.index !== undefined) {
    after = after.slice(0, commentMatch.index);
  }
  const exprSource = after.trim();
  if (exprSource.length === 0) {
    throw new ParseError('"forEach" requires an expression after "in"', openLine.lineNum);
  }
  const source = parseExpr(exprSource, {
    line: openLine.lineNum,
    column: startCol + 1,
    limits: pctx.limits,
  });

  const result = parseStepsBody(pctx, depth + 1, new Set(['end']));
  if (!result.terminator || result.terminator.kw !== 'end') {
    throw new ParseError('"forEach" block was not closed — expected "end"', openLine.lineNum);
  }
  return {
    type: 'forEach',
    varName: nameTok.value,
    source,
    body: result.steps,
  };
}

function parseTryBlock(
  pctx: ParserCtx,
  openLine: { tokens: Token[]; lineNum: number },
  depth: number,
): TryStep {
  requireV2(pctx, 'try', openLine.lineNum);

  const tryRes = parseStepsBody(pctx, depth + 1, new Set(['catch', 'finally', 'end']));
  if (!tryRes.terminator) {
    throw new ParseError('"try" block was not closed — expected "end"', openLine.lineNum);
  }

  const out: TryStep = { type: 'try', body: tryRes.steps };

  if (tryRes.terminator.kw === 'catch') {
    // Optional bareword binds the caught error.
    const nameTok = tryRes.terminator.tokens[1];
    if (nameTok && nameTok.kind === 'word') {
      if (!IDENT_PATTERN.test(nameTok.value)) {
        throw new ParseError(
          `"catch" name "${nameTok.value}" must match /^[a-zA-Z_][a-zA-Z0-9_-]*$/`,
          tryRes.terminator.lineNum,
        );
      }
      out.catchVar = nameTok.value;
    }
    const catchRes = parseStepsBody(pctx, depth + 1, new Set(['finally', 'end']));
    if (!catchRes.terminator) {
      throw new ParseError('"catch" block was not closed — expected "end"', openLine.lineNum);
    }
    out.catchBody = catchRes.steps;
    if (catchRes.terminator.kw === 'finally') {
      const finRes = parseStepsBody(pctx, depth + 1, new Set(['end']));
      if (!finRes.terminator || finRes.terminator.kw !== 'end') {
        throw new ParseError('"finally" block was not closed — expected "end"', openLine.lineNum);
      }
      out.finallyBody = finRes.steps;
    }
    return out;
  }

  if (tryRes.terminator.kw === 'finally') {
    const finRes = parseStepsBody(pctx, depth + 1, new Set(['end']));
    if (!finRes.terminator || finRes.terminator.kw !== 'end') {
      throw new ParseError('"finally" block was not closed — expected "end"', openLine.lineNum);
    }
    out.finallyBody = finRes.steps;
    return out;
  }

  // terminator is 'end'
  return out;
}

/* ─────────────── body parser (recursive) ───────────────────────── */

function parseStepsBody(
  pctx: ParserCtx,
  depth: number,
  allowedTerminators: ReadonlySet<string>,
): BodyResult {
  if (depth > pctx.limits.maxBlockDepth) {
    throw new ParseError(
      `block nesting exceeds ${pctx.limits.maxBlockDepth}`,
      pctx.cursor.peek()?.line ?? 0,
    );
  }

  const steps: Step[] = [];

  while (!pctx.cursor.isEOF()) {
    const line = pctx.cursor.consumeLogicalLine();
    if (!line) break;
    const { tokens: lineTokens, lineNum: ln } = line;
    if (lineTokens.length === 0) continue;
    if (steps.length >= pctx.limits.maxSteps) {
      throw new ParseError(`tour exceeds ${pctx.limits.maxSteps} steps`, ln);
    }

    const fw = firstWord(lineTokens);
    const kw = fw?.value;
    if (!kw) continue;

    // Block terminators: bubble up to caller.
    if (BLOCK_TERMINATORS.has(kw)) {
      if (!allowedTerminators.has(kw)) {
        throw new ParseError(
          `unexpected "${kw}" — no matching opener`,
          ln,
        );
      }
      return { steps, terminator: { kw, lineNum: ln, tokens: lineTokens } };
    }

    // Block openers: recurse.
    if (BLOCK_OPENERS.has(kw)) {
      let blockStep: Step;
      if (kw === 'if')      blockStep = parseIfBlock(pctx, line, depth);
      else if (kw === 'forEach') blockStep = parseForEachBlock(pctx, line, depth);
      else /* try */       blockStep = parseTryBlock(pctx, line, depth);
      applyCommonStepAttrs(blockStep, lineTokens, ln, pctx);
      steps.push(blockStep);
      continue;
    }

    // Top-level directives: macros and includes are NOT step-producing
    // — they register on the parser context and continue without
    // emitting a Step. Allowed only at top-level (depth 0); nested
    // appearance is a parse error.
    if (TOP_LEVEL_DIRECTIVES.has(kw)) {
      requireV2(pctx, kw, ln);
      if (depth !== 0) {
        throw new ParseError(
          `"${kw}" is only allowed at top level`,
          ln,
        );
      }
      if (kw === 'macro') {
        const def = parseMacroDef(pctx, line, depth);
        if (pctx.macros.has(def.name)) {
          throw new ParseError(`duplicate macro "${def.name}"`, ln);
        }
        if (pctx.macros.size >= pctx.limits.maxMacros) {
          throw new ParseError(`tour exceeds ${pctx.limits.maxMacros} macros`, ln);
        }
        pctx.macros.set(def.name, def);
      } else /* include */ {
        const path = parseIncludePath(line);
        if (path === null) {
          throw new ParseError('"include" requires a quoted path', ln);
        }
        pctx.includes.push(path);
      }
      continue;
    }

    // `call NAME [k:v]…` — macro invocation. Always produces a CallStep.
    if (kw === 'call') {
      requireV2(pctx, 'call', ln);
      const callStep = parseCallStep(pctx, line);
      applyCommonStepAttrs(callStep, lineTokens, ln, pctx);
      steps.push(callStep);
      continue;
    }

    // Decl openers: produce a single Step.
    if (DECL_OPENERS.has(kw)) {
      let declStep: Step;
      if (kw === 'let')      declStep = parseLetDecl(pctx, line);
      else if (kw === 'set') declStep = parseSetDecl(pctx, line);
      else /* capture */     declStep = parseCaptureDecl(pctx, line);
      applyCommonStepAttrs(declStep, lineTokens, ln, pctx);
      steps.push(declStep);
      continue;
    }

    // Loop-control verbs.
    if (LOOP_CONTROL_VERBS.has(kw)) {
      requireV2(pctx, kw, ln);
      const step: Step = { type: kw } as Step;
      applyCommonStepAttrs(step, lineTokens, ln, pctx);
      steps.push(step);
      continue;
    }

    // Regular leaf step.
    const step = parseStep(lineTokens, ln, pctx.limits, pctx.etlVersion);
    applyCommonStepAttrs(step, lineTokens, ln, pctx);
    steps.push(step);
  }

  // Reached EOF.
  return { steps, terminator: null };
}

/** Common per-step attributes: author id, retry, backoff, template strings. */
function applyCommonStepAttrs(
  step: Step,
  tokens: Token[],
  ln: number,
  pctx: ParserCtx,
): void {
  const authored = readAuthorId(tokens, ln, pctx.limits);
  if (authored !== undefined) {
    if (pctx.authorIds.has(authored)) {
      throw new ParseError(`duplicate step id "${authored}"`, ln);
    }
    pctx.authorIds.add(authored);
    step.id = authored;
    // stableId for authored ids is the id itself; auto-assignment for
    // unauthored steps happens after the whole tree is parsed (see
    // {@link assignStableIds}).
    step.stableId = authored;
  }

  const retryOpts = readRetryOptions(tokens, ln, pctx.limits, pctx.etlVersion);
  if (retryOpts.retry !== undefined) step.retry = retryOpts.retry;
  if (retryOpts.backoffMs !== undefined) step.backoffMs = retryOpts.backoffMs;

  // Compile `${…}` templates in narration / text strings. v1 tours
  // never reach here because they have no `etl-version 2` header, but
  // the gate is also enforced at template-parse time (the expression
  // parser is the only consumer of identifiers, and v1 doesn't emit
  // any `narrateTpl`-bearing tour).
  if (pctx.etlVersion === 3) {
    if (step.narrate) {
      const tpl = parseTemplate(step.narrate, { line: ln, limits: pctx.limits });
      if (tpl) (step as { narrateTpl?: Expr }).narrateTpl = tpl;
    }
    if (step.type === 'narrate' && step.text) {
      const tpl = parseTemplate(step.text, { line: ln, limits: pctx.limits });
      if (tpl) step.textTpl = tpl;
    }
    if (step.type === 'type' && step.text) {
      const tpl = parseTemplate(step.text, { line: ln, limits: pctx.limits });
      if (tpl) step.textTpl = tpl;
    }
  }
}

/* ─────────────── macro / include / call parsers (Phase 5) ─────── */

/**
 * Parses a `macro NAME` definition.
 *
 * Form:
 * ```
 *   macro NAME
 *     param p1 string required:true
 *     param p2 number default:0
 *     <body steps using ${params.p1}, etc.>
 *   end
 * ```
 *
 * `param` lines inside a macro body declare the macro's parameters.
 * Other body lines are normal steps. The body is parsed as a fresh
 * step tree — control flow, variables, expects, captures all work
 * exactly as in a tour body.
 */
function parseMacroDef(
  pctx: ParserCtx,
  openLine: { tokens: Token[]; lineNum: number },
  depth: number,
): MacroDef {
  const nameTok = openLine.tokens[1];
  if (!nameTok || nameTok.kind !== 'word' || !IDENT_PATTERN.test(nameTok.value)) {
    throw new ParseError(
      '"macro" requires a bareword name (e.g. "macro openWizard")',
      openLine.lineNum,
    );
  }
  const name = nameTok.value;

  // Phase A: read `param` declarations until the first non-param line.
  // Phase B: that non-param line plus everything until `end` is the
  // macro body. We do this by consuming logical lines manually here,
  // peeking the first word of each.
  const params: ParamDecl[] = [];
  while (!pctx.cursor.isEOF()) {
    const saved = pctx.cursor.getPosition();
    const peekLine = pctx.cursor.consumeLogicalLine();
    if (!peekLine) {
      pctx.cursor.setPosition(saved);
      break;
    }
    if (peekLine.tokens.length === 0) continue;
    const fw = firstWord(peekLine.tokens);
    if (fw?.value === 'param') {
      const decl = parseParamHeader(peekLine.tokens, peekLine.lineNum, pctx.limits);
      if (params.length >= pctx.limits.maxParams) {
        throw new ParseError(`macro exceeds ${pctx.limits.maxParams} params`, peekLine.lineNum);
      }
      if (params.some((p) => p.name === decl.name)) {
        throw new ParseError(
          `duplicate param "${decl.name}" in macro "${name}"`,
          peekLine.lineNum,
        );
      }
      params.push(decl);
      continue;
    }
    // Body starts here — rewind so the body parser sees this line.
    pctx.cursor.setPosition(saved);
    break;
  }

  // Body: walk like any other block, terminator must be `end`.
  const bodyResult = parseStepsBody(pctx, depth + 1, new Set(['end']));
  if (!bodyResult.terminator || bodyResult.terminator.kw !== 'end') {
    throw new ParseError(
      `"macro ${name}" was not closed — expected "end"`,
      openLine.lineNum,
    );
  }

  return { name, params, body: bodyResult.steps };
}

/** Parses an `include "path/to/lib.etlm"` directive. */
function parseIncludePath(line: { tokens: Token[]; lineNum: number }): string | null {
  for (const t of line.tokens) {
    if (t.kind === 'string') return (t as StringToken).value;
  }
  return null;
}

/**
 * Parses a `call NAME [k:v ...]` invocation. Args are key:value option
 * pairs; values are interpreted as expression source so authors can
 * pass `${vars.x}` or other interpolations through. String values use
 * the `key:"…"` form via the existing word-then-string token shape.
 */
function parseCallStep(
  pctx: ParserCtx,
  line: { tokens: Token[]; lineNum: number },
): CallStep {
  const nameTok = line.tokens[1];
  if (!nameTok || nameTok.kind !== 'word' || !IDENT_PATTERN.test(nameTok.value)) {
    throw new ParseError(
      '"call" requires a bareword macro name (e.g. "call openWizard")',
      line.lineNum,
    );
  }
  const macro = nameTok.value;
  const args: Record<string, Expr> = {};

  for (let i = 2; i < line.tokens.length; i++) {
    const t = line.tokens[i];
    // Option form: `key:value`. The value is interpreted as an
    // expression source (so `email:user.email` evaluates user.email
    // at call time). Bare scalar values like `email:plain` resolve
    // to the identifier `plain`, which the evaluator returns
    // undefined for unless it's in scope — that's the right semantic
    // for unbound names.
    if (t.kind === 'option') {
      const o = t as OptionToken;
      args[o.key] = parseExpr(o.value, { line: line.lineNum, limits: pctx.limits });
      continue;
    }
    // Word-then-string form: `key:` + "value".
    if (
      t.kind === 'word' &&
      (t as WordToken).value.endsWith(':') &&
      i + 1 < line.tokens.length &&
      line.tokens[i + 1].kind === 'string'
    ) {
      const key = (t as WordToken).value.slice(0, -1);
      const strLit = line.tokens[i + 1] as StringToken;
      // Treat the quoted value as a template — `${…}` interpolates,
      // plain text becomes a literal.
      const tpl = parseTemplate(strLit.value, { line: line.lineNum, limits: pctx.limits });
      args[key] = tpl ?? { kind: 'lit', value: strLit.value };
      i++;
    }
  }

  return { type: 'call', macro, args };
}

/* ─────────────── tree-aware stableId assignment ────────────────── */

/**
 * Walks the parsed step tree and assigns a deterministic auto-id to
 * any step that doesn't already have one. The path is human-readable
 * (e.g. `auto:0.then.1`, `auto:2.body.0`) so authors can reason about
 * what changed between edits.
 */
function assignStableIds(steps: ReadonlyArray<Step>, prefix: string): void {
  steps.forEach((step, idx) => {
    if (step.stableId === undefined) {
      step.stableId = prefix === '' ? `auto:${idx.toString(36)}` : `${prefix}.${idx.toString(36)}`;
    }
    const childPrefix = step.stableId;
    if (step.type === 'if') {
      step.branches.forEach((b, i) => {
        const tag = i === 0 ? 'then' : `elseif${i}`;
        assignStableIds(b.body, `${childPrefix}.${tag}`);
      });
      if (step.otherwise) assignStableIds(step.otherwise, `${childPrefix}.else`);
    } else if (step.type === 'forEach') {
      assignStableIds(step.body, `${childPrefix}.body`);
    } else if (step.type === 'try') {
      assignStableIds(step.body, `${childPrefix}.try`);
      if (step.catchBody) assignStableIds(step.catchBody, `${childPrefix}.catch`);
      if (step.finallyBody) assignStableIds(step.finallyBody, `${childPrefix}.finally`);
    }
  });
}

/* ─────────────────── ETL v2 — header helpers (Phase 4) ─────────── */

const PARAM_TYPES: ReadonlySet<string> = new Set(['string', 'number', 'bool', 'enum']);
const REQUIREMENT_KINDS: ReadonlySet<Requirement['kind']> = new Set<Requirement['kind']>([
  'role', 'permission', 'flag', 'plan', 'env',
]);
const SCOPE_AXES: ReadonlySet<keyof ScopeFilter> = new Set<keyof ScopeFilter>([
  'tenant', 'plugin', 'role',
]);

/**
 * Parses one `param` header line.
 *
 * Form: `param NAME TYPE [default:VALUE] [required:true|false] [enum:V1,V2,...]`
 *
 * Multiple `param` lines per tour build the `params` declaration list
 * in declaration order. Per-line shape mirrors existing header
 * keywords (`pace settle:N narrate:N`) so the lexer's option-token
 * machinery handles the heavy lifting.
 */
function parseParamHeader(tokens: Token[], ln: number, limits: EtlLimits): ParamDecl {
  // tokens[0] = word("param"). Scan for the bareword name and type.
  let nameTok: WordToken | null = null;
  let typeTok: WordToken | null = null;
  for (let i = 1; i < tokens.length; i++) {
    const t = tokens[i];
    if (t.kind !== 'word') continue;
    if (!nameTok) { nameTok = t as WordToken; continue; }
    if (!typeTok) { typeTok = t as WordToken; break; }
  }
  if (!nameTok || !IDENT_PATTERN.test(nameTok.value)) {
    throw new ParseError(
      '"param" requires a bareword name (e.g. "param userId string")',
      ln,
    );
  }
  if (nameTok.value.length > limits.maxIdentifierLength) {
    throw new ParseError(`param name exceeds ${limits.maxIdentifierLength} chars`, ln);
  }
  if (!typeTok || !PARAM_TYPES.has(typeTok.value)) {
    throw new ParseError(
      `"param ${nameTok.value}" requires a type — one of: string, number, bool, enum`,
      ln,
    );
  }

  const enumOption = findOption(tokens, 'enum');
  const requiredOption = findOption(tokens, 'required');
  const defaultOption = readDefaultValue(tokens);

  let type: ParamDecl['type'];
  if (typeTok.value === 'enum') {
    if (enumOption === null || enumOption.length === 0) {
      throw new ParseError(
        '"param … enum" requires `enum:V1,V2,...` listing the allowed values',
        ln,
      );
    }
    const members = enumOption
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (members.length === 0) {
      throw new ParseError('"enum:" must list at least one value', ln);
    }
    type = { enum: members };
  } else {
    type = typeTok.value as 'string' | 'number' | 'bool';
  }

  const out: ParamDecl = { name: nameTok.value, type };

  if (requiredOption !== null) {
    if (requiredOption !== 'true' && requiredOption !== 'false') {
      throw new ParseError(`required: must be true or false (got "${requiredOption}")`, ln);
    }
    if (requiredOption === 'true') (out as { required?: boolean }).required = true;
  }

  if (defaultOption !== undefined) {
    const coerced = coerceDefault(defaultOption, type, ln);
    (out as { default?: string | number | boolean }).default = coerced;
  }

  return out;
}

/**
 * Reads a `default:…` option value. Accepts both the option-token form
 * (`default:value`) and the word-then-string form (`default:"value"`)
 * since the lexer treats the latter as `word("default:")` +
 * `string("value")`.
 */
function readDefaultValue(tokens: Token[]): string | undefined {
  // Option form first.
  const opt = findOption(tokens, 'default');
  if (opt !== null) return opt;
  // Word-then-string form.
  for (let i = 0; i < tokens.length - 1; i++) {
    const t = tokens[i];
    const n = tokens[i + 1];
    if (t.kind === 'word' && t.value === 'default:' && n.kind === 'string') {
      return (n as StringToken).value;
    }
  }
  return undefined;
}

function coerceDefault(raw: string, type: ParamDecl['type'], ln: number): string | number | boolean {
  if (typeof type === 'object' && 'enum' in type) {
    if (!type.enum.includes(raw)) {
      throw new ParseError(
        `default "${raw}" is not a member of enum[${type.enum.join(',')}]`,
        ln,
      );
    }
    return raw;
  }
  if (type === 'string') return raw;
  if (type === 'number') {
    const n = Number(raw);
    if (!Number.isFinite(n)) throw new ParseError(`default "${raw}" is not a number`, ln);
    return n;
  }
  if (type === 'bool') {
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    throw new ParseError(`default "${raw}" is not true|false`, ln);
  }
  throw new ParseError(`unknown param type`, ln);
}

/**
 * Parses a `requires` header line.
 *
 * Forms accepted:
 *   `requires permission:identity.user.create`
 *   `requires permission:"identity.user.create"`
 *   `requires role:admin flag:new-onboarding`
 *
 * Multiple key:value pairs per line are allowed; each becomes one
 * {@link Requirement}. Multiple `requires` lines per tour are also
 * allowed (they accumulate).
 */
function parseRequiresHeader(tokens: Token[], ln: number): Requirement[] {
  const out: Requirement[] = [];
  for (let i = 1; i < tokens.length; i++) {
    const t = tokens[i];
    // Option-token form: `key:value` (unquoted)
    if (t.kind === 'option') {
      const o = t as OptionToken;
      if (REQUIREMENT_KINDS.has(o.key as Requirement['kind'])) {
        out.push({ kind: o.key as Requirement['kind'], value: o.value });
      }
      continue;
    }
    // Word-then-string form: `key:` followed by quoted string
    if (
      t.kind === 'word' &&
      (t as WordToken).value.endsWith(':') &&
      i + 1 < tokens.length &&
      tokens[i + 1].kind === 'string'
    ) {
      const key = (t as WordToken).value.slice(0, -1);
      if (REQUIREMENT_KINDS.has(key as Requirement['kind'])) {
        out.push({ kind: key as Requirement['kind'], value: (tokens[i + 1] as StringToken).value });
        i++;
      }
    }
  }
  if (out.length === 0) {
    throw new ParseError(
      '"requires" needs at least one of role:/permission:/flag:/plan:/env: …',
      ln,
    );
  }
  return out;
}

/**
 * Parses an `only-for` / `not-for` header line. Same key:value forms
 * as `requires`, but the recognized axes are tenant / plugin / role.
 */
function parseScopeHeader(tokens: Token[], ln: number): ScopeFilter {
  const out: { tenant?: string[]; plugin?: string[]; role?: string[] } = {};
  const push = (axis: keyof ScopeFilter, value: string): void => {
    if (!out[axis]) out[axis] = [];
    (out[axis] as string[]).push(value);
  };
  for (let i = 1; i < tokens.length; i++) {
    const t = tokens[i];
    if (t.kind === 'option') {
      const o = t as OptionToken;
      if (SCOPE_AXES.has(o.key as keyof ScopeFilter)) {
        push(o.key as keyof ScopeFilter, o.value);
      }
      continue;
    }
    if (
      t.kind === 'word' &&
      (t as WordToken).value.endsWith(':') &&
      i + 1 < tokens.length &&
      tokens[i + 1].kind === 'string'
    ) {
      const key = (t as WordToken).value.slice(0, -1);
      if (SCOPE_AXES.has(key as keyof ScopeFilter)) {
        push(key as keyof ScopeFilter, (tokens[i + 1] as StringToken).value);
        i++;
      }
    }
  }
  if (!out.tenant && !out.plugin && !out.role) {
    throw new ParseError(
      '"only-for"/"not-for" need at least one of tenant:/plugin:/role: …',
      ln,
    );
  }
  return out;
}

function mergeScope(a: ScopeFilter | undefined, b: ScopeFilter): ScopeFilter {
  const out: { tenant?: string[]; plugin?: string[]; role?: string[] } = {
    ...(a as { tenant?: string[]; plugin?: string[]; role?: string[] } | undefined),
  };
  for (const axis of ['tenant', 'plugin', 'role'] as const) {
    if (b[axis] && b[axis]!.length > 0) {
      out[axis] = [ ...(out[axis] ?? []), ...b[axis]! ];
    }
  }
  return out;
}

/** Parses `prerequisites tour:ID tour:OTHER`. */
function parsePrerequisitesHeader(tokens: Token[], ln: number): string[] {
  const out: string[] = [];
  for (let i = 1; i < tokens.length; i++) {
    const t = tokens[i];
    if (t.kind === 'option' && (t as OptionToken).key === 'tour') {
      out.push((t as OptionToken).value);
    }
    if (
      t.kind === 'word' &&
      (t as WordToken).value === 'tour:' &&
      i + 1 < tokens.length &&
      tokens[i + 1].kind === 'string'
    ) {
      out.push((tokens[i + 1] as StringToken).value);
      i++;
    }
  }
  if (out.length === 0) {
    throw new ParseError('"prerequisites" needs at least one `tour:ID`', ln);
  }
  return out;
}

/* ─────────────────── parse cache (LRU) ─────────────────── */

const PARSE_CACHE_CAPACITY = 64;
const parseCache = new Map<string, Tour>();

function cacheGet(key: string): Tour | undefined {
  const v = parseCache.get(key);
  if (v === undefined) return undefined;
  // Touch (move to end) to maintain LRU order.
  parseCache.delete(key);
  parseCache.set(key, v);
  return v;
}

function cacheSet(key: string, value: Tour): void {
  if (parseCache.has(key)) parseCache.delete(key);
  parseCache.set(key, value);
  if (parseCache.size > PARSE_CACHE_CAPACITY) {
    const oldest = parseCache.keys().next().value;
    if (oldest !== undefined) parseCache.delete(oldest);
  }
}

/** Clears the internal LRU cache. Exposed for tests / HMR. */
export function clearParseCache(): void {
  parseCache.clear();
}

/* ─────────────────── public entry points ─────────────────── */

export interface ParseTourOptions {
  readonly limits?: EtlLimits;
  /** Disable the LRU cache for this call. Default: cache enabled. */
  readonly cache?: boolean;
}

function parseTourImpl(source: string, limits: EtlLimits): Tour {
  const tokens = tokenize(source, { limits });
  const cursor = new Cursor(tokens);

  const header = parseHeader(cursor, limits);
  const sourceLines = source.split('\n');
  const pctx: ParserCtx = {
    cursor,
    limits,
    etlVersion: header.etlVersion,
    sourceLines,
    authorIds: new Set<string>(),
    macros: new Map<string, MacroDef>(),
    includes: [],
  };

  // Top-level body: no terminators are allowed (a stray `end` at the
  // top of the tour is an error caught here, not in `parseStepsBody`'s
  // BLOCK_TERMINATORS branch).
  const bodyResult = parseStepsBody(pctx, 0, new Set<string>());
  if (bodyResult.terminator) {
    throw new ParseError(
      `unexpected "${bodyResult.terminator.kw}" — no matching opener`,
      bodyResult.terminator.lineNum,
    );
  }
  const steps = bodyResult.steps;

  // Tree-aware auto-id assignment runs after the whole tour is parsed
  // so paths can be derived from the final structure.
  assignStableIds(steps, '');

  const raw: unknown = {
    id: header.id,
    pluginId: header.pluginId,
    title: header.title ?? header.id,
    ...(header.summary && { summary: header.summary }),
    ...(header.guidePath && { guidePath: header.guidePath }),
    ...(header.matchRoute && { match: { route: header.matchRoute } }),
    ...(header.demoMode && { demoMode: header.demoMode }),
    ...(header.pace && { pace: header.pace }),
    ...(header.etlVersion !== undefined && { etlVersion: header.etlVersion }),
    ...(header.params && header.params.length > 0 && { params: header.params }),
    ...(header.requires && header.requires.length > 0 && { requires: header.requires }),
    ...(header.onlyFor && { onlyFor: header.onlyFor }),
    ...(header.notFor && { notFor: header.notFor }),
    ...(header.prerequisites && header.prerequisites.length > 0 && { prerequisites: header.prerequisites }),
    ...(header.isLibrary && { isLibrary: header.isLibrary }),
    ...(header.onError && { onError: header.onError }),
    ...(pctx.macros.size > 0 && { macros: Object.fromEntries(pctx.macros) }),
    ...(pctx.includes.length > 0 && { includes: pctx.includes }),
    steps,
  };

  return validateTour(raw, { limits });
}

export function parseTour(source: string, options: ParseTourOptions = {}): Tour {
  if (typeof source !== 'string') {
    throw new ParseError('source must be a string', 0);
  }
  const limits = options.limits ?? DEFAULT_LIMITS;
  const useCache = options.cache !== false && limits === DEFAULT_LIMITS;

  if (useCache) {
    const hit = cacheGet(source);
    if (hit) return hit;
  }
  const tour = parseTourImpl(source, limits);
  if (useCache) cacheSet(source, tour);
  return tour;
}

/* ─────────────────── Result-style API ─────────────────── */

export type ParseResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: Error };

/**
 * Non-throwing variant of {@link parseTour}. Useful when handling
 * untrusted input (AI agent responses, plugin uploads) where exception
 * propagation is undesirable on the hot path.
 */
export function parseTourSafe(
  source: string,
  options: ParseTourOptions = {},
): ParseResult<Tour> {
  try {
    return { ok: true, value: parseTour(source, options) };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e : new Error(String(e)) };
  }
}
